import { useEffect, useMemo, useRef, useState } from 'react';
import { dialCodes } from 'variables/dial-codes';
import {
  dialCodeDigits,
  getMaxNationalDigits,
  getPrefixForCountry,
  parsePhoneValue,
  resolvePhoneCountry,
} from '../../utils/phone-input-functions';

export const usePhoneInputState = ({
  name,
  value = '',
  valueFormat = 'digits',
  onChange,
  defaultCountry,
  dataset,
}) => {
  const codes = useMemo(() => dialCodes, []);
  const defaultPrefix = useMemo(
    () => getPrefixForCountry(defaultCountry, codes),
    [defaultCountry, codes],
  );

  const isUserSelectedRef = useRef(false);
  const lastEmittedRef = useRef(null);

  const initial = useMemo(
    () => parsePhoneValue(value, codes, defaultPrefix),
    [value, codes, defaultPrefix],
  );

  const [prefix, setPrefix] = useState(initial.prefix);
  const [number, setNumber] = useState(initial.number);
  const [countryCode, setCountryCode] = useState(() =>
    resolvePhoneCountry({
      prefix: initial.prefix,
      number: initial.number,
      codes,
      defaultCountry,
      allowInference: true,
    }),
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  const buildValue = (nextPrefix, nextNumber) => {
    const prefixDigits = dialCodeDigits(nextPrefix);
    if (valueFormat === 'e164') {
      return nextNumber ? `+${prefixDigits}${nextNumber}` : '';
    }
    return `${prefixDigits}${nextNumber}`;
  };

  const emit = (nextPrefix, nextNumber) => {
    const nextValue = buildValue(nextPrefix, nextNumber);
    lastEmittedRef.current = nextValue;
    onChange({
      target: {
        name,
        value: nextValue,
        ...(dataset ? { dataset } : {}),
      },
    });
  };

  useEffect(() => {
    const parsed = parsePhoneValue(value, codes, defaultPrefix);
    const hasIncomingDigits = Boolean(dialCodeDigits(value));

    if (!hasIncomingDigits && isUserSelectedRef.current) {
      setNumber(parsed.number);
      return;
    }

    const allowInference = value !== lastEmittedRef.current;

    setPrefix(parsed.prefix);
    setNumber(parsed.number);
    setCountryCode((current) =>
      resolvePhoneCountry({
        prefix: parsed.prefix,
        number: parsed.number,
        codes,
        currentCountry: current,
        defaultCountry,
        isUserSelected: isUserSelectedRef.current,
        allowInference,
      }),
    );
  }, [value, codes, defaultPrefix, defaultCountry]);

  const capNationalNumber = (nationalNumber, nextPrefix) =>
    valueFormat === 'e164'
      ? nationalNumber.slice(0, getMaxNationalDigits(nextPrefix))
      : nationalNumber;

  const handleNumberChange = (event) => {
    const nextNumber = capNationalNumber(event.target.value.replace(/\D/g, ''), prefix);

    setNumber(nextNumber);
    emit(prefix, nextNumber);
  };

  const handlePrefixChange = (nextPrefix, nextCountryCode) => {
    isUserSelectedRef.current = true;

    const nextNumber = capNationalNumber(number, nextPrefix);

    setPrefix(nextPrefix);
    setNumber(nextNumber);
    setCountryCode(
      nextCountryCode ||
        resolvePhoneCountry({
          prefix: nextPrefix,
          number: nextNumber,
          codes,
          defaultCountry,
          allowInference: false,
        }),
    );
    emit(nextPrefix, nextNumber);
  };

  return {
    codes,
    prefix,
    number,
    countryCode,
    isModalOpen,
    setIsModalOpen,
    handleNumberChange,
    handlePrefixChange,
  };
};
